import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation,
  OnInit,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { SelectOption } from '@app/core/models/selector-option';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-adv-select-input',
  templateUrl: './advanced-select-input.component.html',
  styleUrls: ['./advanced-select-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AdvancedSelectInputComponent
  implements OnChanges, OnDestroy, OnInit
{
  @ViewChild('resultBox', { static: false }) resultBox: ElementRef;

  private destroy$ = new Subject<boolean>();
  private _filterChanged$ = new Subject<string>();
  private maxWidth = 0;

  @Input()
  noValueText = 'Not selected';
  @Input()
  useDescriptionForDisplay = false;
  @Input()
  parentForm: UntypedFormGroup;

  @Input()
  inputName: string;

  @Input()
  hoverEffect = true;

  @Input()
  externalFilter: boolean;

  @Input()
  items$: Observable<SelectOption[]>;
  @Input()
  labelName: string;

  @Input()
  appendLabel: boolean;

  @Input()
  flagAsRequired: boolean;

  @Output()
  itemSelected$ = new EventEmitter<string>();

  @Output()
  filterChanged$ = new EventEmitter<string>();

  @ViewChild('input') inputElement: ElementRef;
  private selectedDescription: string;

  get value() {
    const value = this.parentForm.get(this.inputName).value;
    return value;
  }

  get formcontrol() {
    const value = this.parentForm.get(this.inputName);
    return value;
  }

  set value(value: any) {
    const currentValue = this.parentForm.get(this.inputName).value;
    this.parentForm.get(this.inputName).setValue(value);
    if (currentValue != value) {
      this.parentForm.markAsDirty();
    }
  }

  filteredItems$: Observable<SelectOption[]>;

  constructor() {}

  ngOnInit() {
    if (this.items$) {
      this.items$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
        const selectedItem = items.find((item) => item.value === this.value);
        if (selectedItem) {
          this.selectedDescription = selectedItem.description;
        }
      });
    }

    this.parentForm
      .get(this.inputName)
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((newValue) => {
        if (this.items$) {
          this.items$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
            const selectedItem = items.find((item) => item.value === newValue);
            this.selectedDescription = selectedItem
              ? selectedItem.description
              : '';
          });
        }
      });
  }

  ngOnChanges() {
    if (this.items$) {
      this.items$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
        const selectedItem = items.find((item) => item.value === this.value);
        if (selectedItem) {
          this.selectedDescription = selectedItem.description;
        }
        items.forEach((item) => {
          const tempSpan = document.createElement('span');
          tempSpan.style.position = 'absolute';
          tempSpan.style.visibility = 'hidden';
          tempSpan.textContent = item.description;
          document.body.appendChild(tempSpan);

          const itemWidth = tempSpan.offsetWidth;
          this.maxWidth = Math.max(this.maxWidth, itemWidth);

          document.body.removeChild(tempSpan);
        });

        this.resultBox.nativeElement.style.minWidth = `${this.maxWidth}px`;
      });
      this.filteredItems$ = this.items$;

      this._filterChanged$.pipe(takeUntil(this.destroy$)).subscribe((text) => {
        if (!this.externalFilter) {
          if (!text) {
            this.filteredItems$ = this.items$;
          } else {
            this.filteredItems$ = this.items$.pipe(
              map((items) =>
                items.filter((item) => {
                  const val = item.value.toString();
                  const description = item.description;
                  return (
                    val.toLowerCase().includes(text.toLowerCase()) ||
                    description.toLowerCase().includes(text.toLowerCase())
                  );
                }),
              ),
            );
          }
        }

        this.filterChanged$.emit(text);
      });
    }
  }

  onItemSelected(item: string, description: string): void {
    this.value = item;
    this.selectedDescription = description;
    this.itemSelected$.emit(item);
  }

  onFilterChanged(event: any): void {
    if (!event.target) {
      return;
    }
    this._filterChanged$.next(event.target.value);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  focusOnInput() {
    setTimeout(() => {
      this.inputElement.nativeElement.focus();
    }, 0);
  }

  getDisplayValue() {
    const description = this.useDescriptionForDisplay
      ? this.selectedDescription
      : this.value;
    return description ? description : this.noValueText;
  }
}
